import _, { isNil } from "lodash";

import { DateRecord } from "../../../../../types/immutableRecords/model/data/DateRecord";
import { TimespanRecord } from "../../../../../types/immutableRecords/model/data/TimespanRecord";
import dateFunctions, { isDateEarlierThan, isDateValid } from "../../../../definition/calendar/dateFunctions";
import dateByAddingSpan from "../../../../definition/calendar/dateFunctions/dateByAddingSpan";
import dateWithTimestamp from "../../../../definition/calendar/dateFunctions/dateWithTimestamp";
import {
  getReverseTimespan,
  timespanForPrecision
} from "../../../../definition/calendar/dateFunctions/timespanFunctions";
import { setItemDurationToEarliestEnd } from "./setItemDurationToEarliestEnd";
import { setItemPreferredDuration } from "./setItemPreferredDuration";

const calculateStartDateByEndDate = (calendar, endTimestamp, endPrecision, duration) => {
  const endDate = dateFunctions.dateWithTimestamp(calendar, endTimestamp, endPrecision);
  const startDate = isDateValid(endDate)
    ? dateFunctions.dateByAddingSpan(calendar, endDate, getReverseTimespan(duration))
    : DateRecord();
  return startDate;
};

const calculateDurationByEndDate = (startDate, timestamp, precision, calendar, allowZeroDuration) => {
  let duration = null;
  if (isDateValid(startDate)) {
    // Add a minimum duration based on the precision
    // if the end date timestamp is the same as that of the start date
    if (startDate.timestamp === timestamp) {
      duration = allowZeroDuration ? TimespanRecord() : timespanForPrecision(Math.max(startDate.precision, precision));
    } else {
      duration = !isNil(timestamp)
        ? dateFunctions.timespanBetweenDates(
          calendar,
          dateFunctions.dateWithTimestamp(calendar, startDate.timestamp, startDate.precision),
          dateFunctions.dateWithTimestamp(calendar, timestamp, precision)
        )
        : null;
    }
  }
  return duration;
};

export const setEndDate = (obj, timestamp, precision, calendar, startDateKey, durationKey) => {
  const startDate = obj[startDateKey];
  const { durationToEarliestEnd } = obj;
  let newObj = obj;

  let targetEndDate = null;
  // When clearing preferred end date,
  if (_.isNil(timestamp) && durationKey === "preferredDuration") {
    // Set target end date to the earliest end date if that is available,
    // And clear the earliest end date
    if (durationToEarliestEnd) {
      targetEndDate = dateByAddingSpan(calendar, startDate, durationToEarliestEnd);
      newObj = setItemEarliestEndDate(newObj, null, null, calendar);
    } else {
      targetEndDate = startDate;
    }
  } else {
    targetEndDate = _.isNil(timestamp) ? DateRecord() : dateWithTimestamp(calendar, timestamp, precision);
  }

  // If the item already has a start date, then we are setting the duration here
  if (isDateValid(startDate)) {
    // end date must be later than latest start if it is existed
    const latestStartDate = obj.latestStartDate;
    if (
      isDateValid(latestStartDate) &&
      isDateValid(targetEndDate) &&
      !isDateEarlierThan(calendar, latestStartDate, targetEndDate)
    ) {
      return newObj;
    }

    const calculatedDuration = calculateDurationByEndDate(
      startDate,
      _.isNil(timestamp) ? timestamp : targetEndDate.timestamp,
      targetEndDate.precision,
      calendar,
      durationKey === "durationToEarliestEnd" ? true : false
    );

    if (durationKey === "preferredDuration") {
      return setItemPreferredDuration(newObj, calculatedDuration, calendar);
    } else if (durationKey === "durationToEarliestEnd") {
      return setItemDurationToEarliestEnd(newObj, calculatedDuration, calendar);
    } else {
      return newObj.set(durationKey, calculatedDuration);
    }
  } else {
    // Otherwise, there is no start date... so we actually need to keep the duration
    // the same, but calculate an end date
    return newObj.set(startDateKey, calculateStartDateByEndDate(calendar, timestamp, precision, obj[durationKey]));
  }
};

export const setEndDateAndUpdate = (obj, timestamp, precision, calendar, startDateKey, durationKey, endDateKey) => {
  const result = setEndDate(obj, timestamp, precision, calendar, startDateKey, durationKey);

  const start = result.get(startDateKey);
  const duration = result.get(durationKey);
  if (isDateValid(start) && duration) {
    return result.set(
      endDateKey,
      dateFunctions.dateByAddingSpan(calendar, result.get(startDateKey), result.get(durationKey))
    );
  } else {
    return result.set(endDateKey, null);
  }
};

export const setItemEndDate = (obj, timestamp, precision, calendar) => {
  return setEndDateAndUpdate(obj, timestamp, precision, calendar, "startDate", "duration", "endDate");
};

export const setItemPreferredEndDate = (obj, timestamp, precision, calendar) => {
  return setEndDateAndUpdate(
    obj,
    timestamp,
    precision,
    calendar,
    "preferredStartDate",
    "preferredDuration",
    "preferredEndDate"
  );
};

export const setItemEarliestEndDate = (obj, timestamp, precision, calendar) => {
  return setEndDateAndUpdate(
    obj,
    timestamp,
    precision,
    calendar,
    "preferredStartDate",
    "durationToEarliestEnd",
    "earliestEndDate"
  );
};
